import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';

import { DecimalInput } from '../DecimalInput';
import { TotemLabel } from '../TotemLabel';

// Générique sur la forme du formulaire, comme `DetailFormDatePicker` : une page qui type son `useForm`
// passe son `control` et son `name` sans les élargir.
type DetailFormDecimalProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    label?: string;
    sublabel?: string;
    placeholder?: string;
    name: TName;
    defaultValue?: FieldPathValue<TFieldValues, TName>;
    control: Control<TFieldValues>;
    isDisabled?: boolean;
    error?: string;
    unit?: string;
    dataTest?: string;
};

/** Un champ de fiche qui porte un nombre à décimales libres : prix facturé, température… */
export const DetailFormDecimal = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    label,
    sublabel,
    placeholder = 'Aucune valeur',
    name,
    defaultValue,
    control,
    isDisabled,
    error,
    unit,
    dataTest = `decimal-input-${name}`,
}: DetailFormDecimalProps<TFieldValues, TName>) => {
    return (
        <div className="flex flex-col text-foreground">
            <div className="flex flex-col">
                {label ? <TotemLabel>{label}</TotemLabel> : null}
                {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
            </div>
            <div className="relative mt-[5px]">
                <Controller
                    control={control}
                    name={name}
                    defaultValue={defaultValue}
                    render={({ field: { onBlur, onChange, ref, value } }) => (
                        <DecimalInput
                            ref={ref}
                            value={value ?? null}
                            onChange={onChange}
                            onBlur={onBlur}
                            disabled={isDisabled}
                            placeholder={placeholder}
                            aria-label={label}
                            data-test={dataTest}
                            className="h-auto px-3 py-3 text-base"
                        />
                    )}
                />
                {unit ? (
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-base text-muted-foreground">
                        {unit}
                    </span>
                ) : null}
                {error ? (
                    <div className="absolute right-[-2px] top-[-16px] text-base text-destructive w-max">{error}</div>
                ) : null}
            </div>
        </div>
    );
};
